/**
 * FinancialSyncPage — workspace landing.
 *
 * Replaces the cramped Excel upload modal with a dedicated page.
 * Phase-1 vertical slice ships ONLY the P&L slot end-to-end; BS / TB
 * are visible as locked slots so the customer sees the destination.
 */
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// safeJson — never let a raw JSON.parse / WebKit "SyntaxError: The string
// did not match the expected pattern" surface to the user. Categorize
// every failure and let callers route to friendly copy.
// ---------------------------------------------------------------------------
async function safeFetchJson(url, init) {
  let resp;
  try {
    resp = await fetch(url, { credentials: "include", ...init });
  } catch (e) {
    return { ok: false, kind: "network", status: 0, message:
      "We couldn't reach C-ORE. Check your connection and try again." };
  }
  let body = null;
  let parseError = null;
  const ct = (resp.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) {
    try { body = await resp.json(); }
    catch (e) { parseError = e; }
  } else {
    // Read text so we can log it (never shown to user).
    try { await resp.text(); } catch (_e) { /* ignore */ }
  }
  if (!resp.ok) {
    if (resp.status === 401 || resp.status === 403) {
      return { ok: false, kind: "auth", status: resp.status, body, message:
        "Your session expired. Please sign in again." };
    }
    if (resp.status >= 500) {
      return { ok: false, kind: "server", status: resp.status, body, message:
        "Something on our side went wrong. Our team has been notified — please try again in a moment." };
    }
    return { ok: false, kind: "client", status: resp.status, body,
      message: body?.detail || "We couldn't process this request." };
  }
  if (parseError || body === null) {
    return { ok: false, kind: "non_json", status: resp.status, body: null, message:
      "We received an unexpected response. Please try again or use manual locate." };
  }
  return { ok: true, kind: "ok", status: resp.status, body };
}

// Read the customer id from the same sessionStorage slot the login flow
// writes to. Returns null for visitor / demo sessions.
function readCustomerId() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem("current_customer");
    if (!raw) return null;
    const c = JSON.parse(raw);
    return c?.id || c?.customer_id || null;
  } catch (_e) { return null; }
}

export default function FinancialSyncPage() {
  // Phase 1 (TB inclusive) only ships the Accounting Statements path.
  // The Operational Data bucket is intentionally hidden until P2 lands
  // — showing a "Coming in Phase 2" card to every user without a
  // working flow behind it created confusion and false expectations.
  // We re-enable the two-bucket landing once the operational mapper
  // is ready to ship (see ROADMAP — P2 Operational Boardroom).
  const [bucket, setBucket] = useState("financial");
  const [hubStatus, setHubStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const customerId = readCustomerId();

  useEffect(() => {
    if (!customerId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const r = await safeFetchJson(
        `${API}/api/financial-sync/hub-status?customer_id=${encodeURIComponent(customerId)}`,
      );
      if (!cancelled) {
        setHubStatus(r.ok ? r.body : null);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  if (loading) {
    return (
      <div className="px-6 py-10 text-[13px] text-slate-500"
           data-testid="financial-sync-loading">
        Loading…
      </div>
    );
  }

  // Single-bucket experience: skip the landing entirely. When P2
  // ships, restore the BucketLanding by initialising bucket=null.
  return (
    <FinancialHub
      hubStatus={hubStatus}
      onBack={null}
      onUpdated={(next) => setHubStatus(next)}
    />
  );
}


// ---------------------------------------------------------------------------
// Bucket landing — the two-bucket fork (PART 7 of the spec)
// ---------------------------------------------------------------------------
function BucketLanding({ onPick, hubStatus }) {
  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-5xl mx-auto"
         data-testid="financial-sync-bucket-landing">
      <h1 className="text-3xl font-semibold text-slate-900 mb-1">
        Financial Sync
      </h1>
      <p className="text-[14px] text-slate-600 mb-8 max-w-2xl">
        Bring your books into C-ORE by uploading the statements your
        accounting software already produces. We read the figures —
        we never recompute them.
      </p>

      <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2 gap-5">
        <button
          type="button"
          onClick={() => onPick("financial")}
          data-testid="bucket-financial"
          className="text-left rounded-2xl border border-slate-200 bg-white p-6
                     hover:border-emerald-400 hover:shadow-md transition-all"
        >
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">
            Primary path
          </div>
          <div className="text-xl font-semibold text-slate-900 mt-1">
            Accounting Statements
          </div>
          <div className="text-[13px] text-slate-600 mt-2">
            Upload Profit &amp; Loss, Balance Sheet and Trial Balance from
            Tally, BUSY, Zoho Books or any spreadsheet. Activates Virtual
            CFO and Business Health.
          </div>
          {hubStatus && (
            <div className="mt-4 flex items-center gap-2 text-[12px]">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-slate-700">
                Financial Confidence:{" "}
                <strong>{hubStatus.financial_confidence ?? 0}%</strong>
              </span>
            </div>
          )}
        </button>

        <button
          type="button"
          onClick={() => onPick("operational")}
          data-testid="bucket-operational"
          className="text-left rounded-2xl border border-slate-200 bg-white p-6
                     hover:border-blue-400 hover:shadow-md transition-all"
        >
          <div className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold">
            Operational data
          </div>
          <div className="text-xl font-semibold text-slate-900 mt-1">
            Sales / HR / Inventory
          </div>
          <div className="text-[13px] text-slate-600 mt-2">
            Upload CRM exports, payroll registers, inventory lists with
            assisted column mapping. Feeds the non-financial modules.
          </div>
          <div className="mt-4 text-[12px] text-slate-500">
            Coming in Phase 2.
          </div>
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Financial hub — slot checklist + confidence meter (PART 7)
// ---------------------------------------------------------------------------
function FinancialHub({ hubStatus, onBack, onUpdated }) {
  const [openSlot, setOpenSlot] = useState(null);  // "pl" | null

  const refresh = async () => {
    const cid = readCustomerId();
    if (!cid) return;
    const r = await safeFetchJson(
      `${API}/api/financial-sync/hub-status?customer_id=${encodeURIComponent(cid)}`,
    );
    if (r.ok) onUpdated(r.body);
  };

  // SlotSubFlow handles PL, BS and TB uploads (P1.4 wired TB).
  // Without this gate, clicking a slot card would set openSlot but
  // render nothing — repro of the bug we hit on the BS slot rollout.
  if (openSlot === "pl" || openSlot === "bs" || openSlot === "tb") {
    return (
      <SlotSubFlow
        slot={openSlot}
        onBack={() => setOpenSlot(null)}
        onCommitted={async () => {
          await refresh();
          setOpenSlot(null);
        }}
      />
    );
  }

  const slots = hubStatus?.slots || {};
  const conf = hubStatus?.financial_confidence ?? 0;

  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-5xl mx-auto"
         data-testid="financial-hub">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          data-testid="financial-hub-back"
          className="text-[12px] text-slate-500 hover:text-slate-700 mb-3"
        >
          ← Back
        </button>
      )}

      <h1 className="text-3xl font-semibold text-slate-900 mb-1">
        Accounting Statements
      </h1>
      <p className="text-[14px] text-slate-600 mb-6 max-w-2xl">
        Upload the three core statements to activate your Boardroom.
        We&apos;ll show you what we found and ask you to confirm before
        anything reaches your dashboard.
      </p>

      <ConfidenceMeter value={conf}
                       activated={hubStatus?.boardroom_activated} />

      <div className="mt-8 space-y-3">
        <Slot
          slotId="pl"
          title="Profit &amp; Loss"
          subtitle="Required to activate CFO"
          status={slots.pl}
          onOpen={() => setOpenSlot("pl")}
          enabled
        />
        <Slot
          slotId="bs"
          title="Balance Sheet"
          subtitle="Upload your latest Balance Sheet"
          status={slots.bs}
          onOpen={() => setOpenSlot("bs")}
          enabled
        />
        <Slot
          slotId="tb"
          title="Trial Balance"
          subtitle="Confirms your books tally (Dr = Cr)"
          status={slots.tb}
          onOpen={() => setOpenSlot("tb")}
          enabled
        />
      </div>
    </div>
  );
}


function ConfidenceMeter({ value, activated }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5"
         data-testid="financial-confidence-meter">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[13px] uppercase tracking-wider text-slate-500">
          Financial Confidence
        </div>
        <div className={`text-2xl font-semibold ${
          activated ? "text-emerald-600" : "text-slate-400"
        }`}>
          {value ?? 0}%
        </div>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${activated ? "bg-emerald-500" : "bg-slate-300"}`}
             style={{ width: `${Math.min(100, value ?? 0)}%` }} />
      </div>
      <div className="text-[12px] text-slate-600 mt-3">
        {activated
          ? "Your Boardroom is live with this statement's figures."
          : "Upload your Profit & Loss to activate your Boardroom."}
      </div>
    </div>
  );
}


function Slot({ slotId, title, subtitle, status, onOpen, enabled }) {
  const s = status || {};
  const isValidated = s.status === "validated";
  return (
    <div
      data-testid={`slot-${slotId}`}
      className={`rounded-xl border bg-white p-5 flex items-center gap-4
        ${enabled ? "border-slate-200 hover:border-emerald-400 hover:shadow-sm cursor-pointer transition-all"
                  : "border-slate-200 opacity-60"}`}
      onClick={enabled ? onOpen : undefined}
      role={enabled ? "button" : undefined}
      tabIndex={enabled ? 0 : undefined}
      onKeyDown={(e) => {
        if (enabled && (e.key === "Enter" || e.key === " ")) onOpen?.();
      }}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center
        ${isValidated ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-400"}`}>
        {isValidated ? "✓" : "+"}
      </div>
      <div className="flex-1">
        <div className="text-[15px] font-semibold text-slate-900"
             dangerouslySetInnerHTML={{ __html: title }} />
        <div className="text-[12px] text-slate-500">
          {isValidated
            ? `${s.period_fy || "Period saved"} · v${s.statement_version || 1}`
            : subtitle}
        </div>
      </div>
      {enabled && (
        <div className="text-[12px] text-emerald-700 font-medium"
             data-testid={`slot-${slotId}-action`}>
          {isValidated ? "Re-upload" : "Upload"}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Slot sub-flow: drop → preview → confirm → commit (PART 7 B3)
// ---------------------------------------------------------------------------
function SlotSubFlow({ slot, onBack, onCommitted }) {
  const [phase, setPhase] = useState("drop");      // drop | preview | conflict | committing | done | error | manual_locate
  const [parseResp, setParseResp] = useState(null);
  const [error, setError] = useState(null);       // { message, kind }
  const [committed, setCommitted] = useState(null);
  const [liveConflict, setLiveConflict] = useState(null); // canonical-check result

  const customerId = readCustomerId();

  const handleFile = async (file) => {
    if (!file) return;
    if (!customerId) {
      setError({ kind: "auth", message:
        "We can't identify your account. Please sign in again." });
      setPhase("error");
      return;
    }
    setPhase("preview");
    setError(null);

    const fd = new FormData();
    fd.append("file", file);
    fd.append("slot", slot);

    const r = await safeFetchJson(`${API}/api/financial-sync/parse`, {
      method: "POST",
      body: fd,
    });

    if (!r.ok) {
      setError({ kind: r.kind, message: r.message });
      setPhase("error");
      return;
    }

    // Structured graceful failure from the backend reader pipeline.
    if (r.body?.parse_failed) {
      setError({ kind: "reader_failed", message: r.body.hint ||
        "We couldn't read this file automatically. You can locate the rows manually." });
      setPhase("manual_locate");
      return;
    }

    // Multi-period file (e.g. monthly P&Ls pivoted across columns).
    // The locator would silently pick the leftmost month — refuse here
    // so the user re-exports a single period.
    if (r.body?.multi_period) {
      const periods = (r.body.periods_detected || []).join(", ");
      setError({ kind: "multi_period", message:
        (r.body.errors && r.body.errors[0]) ||
        `This file contains multiple periods (${periods}). Please re-export one period at a time.` });
      setPhase("manual_locate");
      return;
    }

    setParseResp(r.body);

    // Canonical Selection Model: check whether the customer already has a
    // LIVE (Tally / connector) source. If so, committing this manual upload
    // will show a conflict dialog before writing.
    try {
      const c = await safeFetchJson(
        `${API}/api/financial-sync/canonical-check?customer_id=${encodeURIComponent(customerId)}`,
      );
      setLiveConflict(c.ok && c.body?.live_source_exists ? c.body : null);
    } catch (e) {
      setLiveConflict(null);
    }
  };

  const doCommit = async (forceCanonical) => {
    if (!parseResp) return;
    if (!customerId) {
      setError({ kind: "auth", message:
        "Your session expired. Please sign in again." });
      setPhase("error");
      return;
    }
    setPhase("committing");

    const r = await safeFetchJson(`${API}/api/financial-sync/commit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        parse_id:        parseResp.parse_id,
        force_canonical: !!forceCanonical,
      }),
    });

    if (!r.ok) {
      setError({ kind: r.kind, message: r.message });
      setPhase("error");
      return;
    }
    if (!r.body?.committed) {
      const messages = (r.body?.messages || [])
        .map((m) => m.message || String(m))
        .join(" ");
      setError({ kind: "validation",
        message: `Validation blocked the commit. ${messages}` });
      setPhase("error");
      return;
    }
    setCommitted(r.body);
    setPhase("done");
  };

  const handleCommit = async () => {
    // If a LIVE source already exists, surface the conflict dialog first.
    if (liveConflict?.live_source_exists) {
      setPhase("conflict");
      return;
    }
    await doCommit(false);
  };

  if (phase === "drop") return (
    <DropZone onBack={onBack} onFile={handleFile} slot={slot} />
  );
  if (phase === "preview" && !parseResp) return (
    <div className="p-10 text-center text-slate-500"
         data-testid="slot-parsing">
      Reading your file…
    </div>
  );
  if (phase === "preview" && parseResp) return (
    <PreviewPanel
      parseResp={parseResp}
      onBack={() => { setPhase("drop"); setParseResp(null); }}
      onConfirm={handleCommit}
    />
  );
  if (phase === "conflict") return (
    <ConflictDialog
      slot={slot}
      liveConflict={liveConflict}
      onKeepLive={() => doCommit(false)}
      onOverride={() => doCommit(true)}
      onCancel={() => { setPhase("drop"); setParseResp(null); setLiveConflict(null); }}
    />
  );
  if (phase === "committing") return (
    <div className="p-10 text-center text-slate-500"
         data-testid="slot-committing">
      Saving and activating your Boardroom…
    </div>
  );
  if (phase === "done") return (
    <CommittedPanel committed={committed} onContinue={onCommitted} />
  );
  if (phase === "manual_locate") return (
    <ManualLocatePanel
      error={error}
      onTryAgain={() => { setPhase("drop"); setError(null); }}
      onBack={onBack}
    />
  );
  return (
    <ErrorPanel
      error={error}
      onRetry={() => { setPhase("drop"); setError(null); }}
      onManual={() => setPhase("manual_locate")}
    />
  );
}


function ConflictDialog({ slot, liveConflict, onKeepLive, onOverride, onCancel }) {
  const sourceLabel = (liveConflict?.live_source || "").toLowerCase().startsWith("tally")
    ? "live Tally" : "live";
  const slotLabel = slot === "bs" ? "Balance Sheet" : slot === "tb" ? "Trial Balance" : "P&L";
  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-2xl mx-auto" data-testid="slot-conflict-dialog">
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              You already have {sourceLabel} data connected
            </h2>
            <p className="mt-1 text-[13px] leading-relaxed text-slate-700">
              Your dashboard is currently powered by your {sourceLabel} source, which
              updates automatically. This uploaded {slotLabel} is a manual snapshot.
              How would you like to use it?
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <button
            type="button"
            onClick={onKeepLive}
            className="w-full rounded-xl border border-emerald-300 bg-white px-4 py-3 text-left hover:border-emerald-500 transition-colors"
            data-testid="conflict-keep-live"
          >
            <div className="text-[14px] font-semibold text-slate-900">
              Keep {sourceLabel} data as my dashboard source
              <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                Recommended
              </span>
            </div>
            <div className="text-[12px] text-slate-600">
              Save this upload as history — your dashboard keeps reading the live source.
            </div>
          </button>

          <button
            type="button"
            onClick={onOverride}
            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-left hover:border-slate-500 transition-colors"
            data-testid="conflict-override"
          >
            <div className="text-[14px] font-semibold text-slate-900">
              Use this uploaded {slotLabel} as my source instead
            </div>
            <div className="text-[12px] text-slate-600">
              Your dashboard will read this upload until your next live sync overrides it.
            </div>
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-slate-400 transition-colors"
            data-testid="conflict-cancel"
          >
            <div className="text-[14px] font-semibold text-slate-900">
              Cancel this upload
            </div>
            <div className="text-[12px] text-slate-600">
              Discard the file and go back to the hub.
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}



function DropZone({ onBack, onFile, slot }) {
  const [drag, setDrag] = useState(false);
  const isBs = slot === "bs";
  const isTb = slot === "tb";
  const headline = isTb ? "Upload your Trial Balance"
                  : isBs ? "Upload your Balance Sheet"
                         : "Upload your Profit & Loss";
  let hint;
  if (isTb) {
    hint = (<>In Tally: <strong>Gateway → Display More Reports → Trial Balance → set the “As on” date → Alt+E (Export) → Excel</strong>. Make sure the Grand Total row is included. Or upload the same export from BUSY, Zoho Books, or any spreadsheet.</>);
  } else if (isBs) {
    hint = (<>In Tally: <strong>Gateway → Reports → Balance Sheet → set the “As on” date → Alt+E (Export) → Excel</strong>. Or upload the same export from BUSY, Zoho Books, or any spreadsheet.</>);
  } else {
    hint = (<>In Tally: <strong>Gateway → Display More Reports → Profit &amp; Loss A/c → Alt+E (Export) → Excel</strong>. Or upload the same export from BUSY, Zoho Books, or any spreadsheet.</>);
  }
  const dropCopy = isTb ? "Drop your Trial Balance file here"
                  : isBs ? "Drop your Balance Sheet file here"
                         : "Drop your P&L file here";
  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-3xl mx-auto"
         data-testid="slot-dropzone">
      <button
        type="button"
        onClick={onBack}
        className="text-[12px] text-slate-500 hover:text-slate-700 mb-3"
        data-testid="slot-dropzone-back"
      >
        ← Back to hub
      </button>

      <h2 className="text-2xl font-semibold text-slate-900 mb-2">
        {headline}
      </h2>
      <p className="text-[13px] text-slate-600 mb-5">
        {hint}
      </p>

      <label
        htmlFor="financial-sync-file-input"
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (e.dataTransfer.files?.[0]) onFile(e.dataTransfer.files[0]);
        }}
        className={`flex flex-col items-center justify-center rounded-2xl
          border-2 border-dashed p-12 cursor-pointer transition-colors
          ${drag ? "border-emerald-500 bg-emerald-50"
                 : "border-slate-300 bg-slate-50 hover:border-slate-400"}`}
        data-testid="slot-dropzone-label"
      >
        <div className="text-[16px] text-slate-700 font-medium">
          {dropCopy}
        </div>
        <div className="text-[12px] text-slate-500 mt-1">
          .xlsx · .xls · .csv — max 5 MB
        </div>
        <input
          id="financial-sync-file-input"
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          data-testid="slot-file-input"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </label>
    </div>
  );
}


function PreviewPanel({ parseResp, onBack, onConfirm }) {
  const figures = parseResp?.figures || {};
  const period = parseResp?.period || {};
  const errors = parseResp?.errors || [];

  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-3xl mx-auto"
         data-testid="slot-preview-panel">
      <h2 className="text-2xl font-semibold text-slate-900 mb-1">
        Confirm the figures we found
      </h2>
      <p className="text-[13px] text-slate-600 mb-5">
        Period detected: <strong>
          {period.period_fy || period.fy ||
           `${period.start_date || "—"} to ${period.end_date || "—"}`}
        </strong>
        {" · "}
        {period.frequency || "Annual"}
      </p>

      {errors.length > 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4"
             data-testid="slot-preview-warnings">
          {errors.map((e, i) => (
            <div key={i} className="text-[13px] text-amber-800">{e}</div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {Object.entries(figures).map(([bucket, fig]) => (
          <FigureRow key={bucket} bucket={bucket} fig={fig} />
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="slot-preview-cancel"
          className="px-4 py-2 text-[13px] text-slate-700 hover:bg-slate-100 rounded-lg"
        >
          Choose different file
        </button>
        <button
          type="button"
          onClick={onConfirm}
          data-testid="slot-preview-confirm"
          className="px-5 py-2 text-[13px] font-medium text-white bg-emerald-600
                     hover:bg-emerald-700 rounded-lg"
        >
          Confirm &amp; save
        </button>
      </div>
    </div>
  );
}


function FigureRow({ bucket, fig }) {
  // The backend returns trace fields flat at the top of `fig`
  // (see statement_reader.locator.Located.to_trace()). There is NO
  // nested `fig.trace`. Reading `fig.trace.X` returns undefined and
  // collapses every row to the "manual" branch even on a green match.
  const value = fig?.value;
  const threshold = fig?.threshold || "manual";
  const matchedLabel = fig?.matched_label || "";
  const rowNum = fig?.row;
  const haveRow = (typeof rowNum === "number" && rowNum >= 0);
  const color = threshold === "green"  ? "text-emerald-700 bg-emerald-50"
              : threshold === "yellow" ? "text-amber-800 bg-amber-50"
              :                          "text-rose-700 bg-rose-50";
  const friendly = {
    revenue:            "Revenue",
    gross_profit:       "Gross Profit",
    net_profit:         "Net Profit",
    operating_expenses: "Operating Expenses",
    other_income:       "Other Income",
    depreciation:       "Depreciation",
    interest_expense:   "Interest Expense",
    tax_expense:        "Tax Expense",
    // Balance Sheet buckets (P1.3)
    total_assets:           "Total Assets",
    current_assets:         "Current Assets",
    fixed_assets:           "Fixed Assets",
    total_liabilities:      "Total Liabilities",
    current_liabilities:    "Current Liabilities",
    long_term_liabilities:  "Long-term Liabilities",
    equity:                 "Equity",
    // Trial Balance buckets (P1.4)
    total_debits:           "Grand Total Debits",
    total_credits:          "Grand Total Credits",
  }[bucket] || bucket;
  return (
    <div className="flex items-center justify-between px-5 py-3 border-b last:border-b-0 border-slate-100"
         data-testid={`figure-row-${bucket}`}>
      <div>
        <div className="text-[14px] font-medium text-slate-900">{friendly}</div>
        <div className="text-[11px] text-slate-500">
          {value == null
            ? "Not found — please correct"
            : (haveRow
                ? `Matched “${matchedLabel || "—"}” · row ${rowNum + 1}`
                : `Matched “${matchedLabel || "—"}”`)}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-[14px] font-mono text-slate-900"
             data-testid={`figure-value-${bucket}`}>
          {value == null ? "—" : `₹ ${Number(value).toLocaleString("en-IN")}`}
        </div>
        <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full ${color}`}>
          {threshold}
        </span>
      </div>
    </div>
  );
}


function CommittedPanel({ committed, onContinue }) {
  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-10 max-w-3xl mx-auto text-center"
         data-testid="slot-committed-panel">
      <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-2xl mb-4">
        ✓
      </div>
      <h2 className="text-2xl font-semibold text-slate-900 mb-2">
        Your Boardroom is live
      </h2>
      <p className="text-[14px] text-slate-600 mb-6">
        CFO and Business Health now read these figures.
      </p>
      <button
        type="button"
        onClick={onContinue}
        data-testid="slot-committed-continue"
        className="px-5 py-2 text-[13px] font-medium text-white bg-emerald-600
                   hover:bg-emerald-700 rounded-lg"
      >
        Continue
      </button>
    </div>
  );
}


function ErrorPanel({ error, onRetry, onManual }) {
  const message = (typeof error === "string")
    ? error
    : (error?.message || "An unexpected error occurred. Please try again.");
  const showManual = typeof onManual === "function";
  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-10 max-w-3xl mx-auto text-center"
         data-testid="slot-error-panel">
      <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 flex items-center justify-center text-rose-700 text-2xl mb-4">
        ⚠
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2">
        We couldn&apos;t save this statement
      </h2>
      <p className="text-[13px] text-slate-600 mb-6 max-w-lg mx-auto"
         data-testid="slot-error-message">
        {message}
      </p>
      <div className="flex items-center justify-center gap-3">
        {showManual && (
          <button
            type="button"
            onClick={onManual}
            data-testid="slot-error-manual-locate"
            className="px-5 py-2 text-[13px] font-medium text-emerald-700 bg-emerald-50
                       hover:bg-emerald-100 rounded-lg border border-emerald-200"
          >
            Locate the rows manually
          </button>
        )}
        <button
          type="button"
          onClick={onRetry}
          data-testid="slot-error-retry"
          className="px-5 py-2 text-[13px] font-medium text-white bg-slate-700
                     hover:bg-slate-900 rounded-lg"
        >
          Try a different file
        </button>
      </div>
    </div>
  );
}


function ManualLocatePanel({ error, onTryAgain, onBack }) {
  const message = error?.message
    || "We couldn't read this file automatically.";
  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-10 max-w-3xl mx-auto"
         data-testid="slot-manual-locate-panel">
      <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-2xl mb-4">
        ?
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2 text-center">
        Let&apos;s locate the rows together
      </h2>
      <p className="text-[13px] text-slate-600 mb-6 max-w-lg mx-auto text-center">
        {message} Manual-locate point-and-pick lands in P1.2.1. In the
        meantime, please re-export your statement with the column
        labels in column A and the values in column B — or share the
        file with our team and we&apos;ll add it as a regression
        fixture so this case parses cleanly next time.
      </p>
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={onTryAgain}
          data-testid="slot-manual-locate-try-again"
          className="px-5 py-2 text-[13px] font-medium text-white bg-emerald-600
                     hover:bg-emerald-700 rounded-lg"
        >
          Try a different file
        </button>
        <button
          type="button"
          onClick={onBack}
          data-testid="slot-manual-locate-back"
          className="px-5 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
        >
          Back to hub
        </button>
      </div>
    </div>
  );
}


function OperationalPathStub({ onBack }) {
  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-10 max-w-3xl mx-auto"
         data-testid="operational-path-stub">
      <button onClick={onBack}
              className="text-[12px] text-slate-500 hover:text-slate-700 mb-3"
              data-testid="operational-back">
        ← Back
      </button>
      <h2 className="text-2xl font-semibold text-slate-900 mb-3">
        Operational Data (Sales · HR · Inventory)
      </h2>
      <p className="text-[13px] text-slate-600">
        Coming in Phase 2. This will feed the non-financial modules
        (CMO / CTO / People / Inventory) with assisted column mapping.
      </p>
    </div>
  );
}
